import React from 'react';
import { Award, Calendar, Clock, Linkedin, Mail, Phone, Users, Star, Shield, Heart, Activity, Layers, CheckCircle } from 'lucide-react';
import MedicalTeamSection from '../components/MedicalTeamSection';

const OurTeam = () => {
  const heroStats = [
    { icon: Users, label: '15+ Expert Doctors', desc: 'Orthopedic, anesthesia & rehab specialists' },
    { icon: Shield, label: '5 Centers of Excellence', desc: 'Joint, spine, trauma, sports & pediatric' },
    { icon: Clock, label: '24/7 Command', desc: 'Rapid response consultants for emergencies' },
    { icon: Award, label: '15,000+ Procedures', desc: 'Advanced surgeries with high success rates' },
  ];

  const workflow = [
    {
      step: '1',
      title: 'Multidisciplinary Assessment',
      desc: 'Orthopedic surgeons, anesthesiologists, and rehab experts create a unified treatment plan.'
    },
    {
      step: '2',
      title: 'Precision Surgery & Monitoring',
      desc: 'Minimally invasive techniques, AI-assisted monitoring, and safe anesthesia protocols.'
    },
    {
      step: '3',
      title: 'Personalized Rehabilitation',
      desc: 'Rehab specialists and physiotherapists guide recovery with tailored home & in-clinic programs.'
    }
  ];

  const cultureHighlights = [
    { icon: Heart, title: 'Family-first counselling', desc: 'Daily ward rounds with families involved in every decision.' },
    { icon: Activity, title: 'Evidence dashboards', desc: 'AI-assisted dashboards track vitals, infection control and pain scores.' },
    { icon: Layers, title: 'Integrated rehab pods', desc: 'Physio, occupational therapy and nutrition consults in one schedule.' }
  ];

  const collaborativeUnits = [
    { title: 'Joint Reconstruction Command', desc: 'Hip, knee and shoulder consultants align prosthesis planning with anesthesia and physio leads.' },
    { title: 'Spine & Neuro Ortho Cell', desc: 'Motion-preserving spine surgeons, pain specialists and ICU nurses run 24/7 coverage.' },
    { title: 'Trauma Rapid Response', desc: 'Dedicated ortho-trauma surgeons, emergency physicians and blood bank coordinators.' },
    { title: 'Sports Medicine Studio', desc: 'Arthroscopy experts, sports rehab trainers and gait analysts for professional athletes.' }
  ];

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 via-blue-900 to-indigo-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-16 w-72 h-72 bg-blue-500/40 rounded-full blur-3xl" />
          <div className="absolute top-10 right-0 w-80 h-80 bg-indigo-500/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-sky-400/25 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left space-y-6">
              <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 border border-blue-200/40 text-sm font-semibold backdrop-blur mb-2">
                <Users className="w-4 h-4 mr-2" />
                Compassionate Specialists
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Meet the team that
                <span className="block bg-gradient-to-r from-white via-blue-100 to-cyan-200 bg-clip-text text-transparent">
                  restores movement and confidence.
                </span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Surgical excellence, anesthetic safety, critical care vigilance, and tailored rehabilitation—under one roof, around the clock.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  disabled
                  className="inline-flex items-center justify-center px-5 py-3 bg-white/80 text-primary-800 font-semibold rounded-xl shadow-md cursor-not-allowed opacity-60"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Speak With Our Coordinator
                </button>
                <button
                  disabled
                  className="inline-flex items-center justify-center px-5 py-3 border border-white/50 text-white/70 font-semibold rounded-xl cursor-not-allowed"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Refer a Patient
                </button>
              </div>
            </div>

            <div className="max-w-md mx-auto">
              <div className="bg-white/10 border border-blue-200/30 rounded-3xl p-6 md:p-7 shadow-2xl backdrop-blur">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm uppercase tracking-wide text-blue-100 font-semibold">
                    Trusted orthopedic team
                  </p>
                  <div className="w-3 h-3 rounded-full bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.8)]"></div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/10 rounded-2xl px-4 py-3 text-center">
                    <Users className="w-5 h-5 mx-auto mb-1 text-blue-100" />
                    <p className="text-xl font-bold">15+</p>
                    <p className="text-xs text-blue-100/90">Expert Doctors</p>
                  </div>
                  <div className="bg-white/10 rounded-2xl px-4 py-3 text-center">
                    <Clock className="w-5 h-5 mx-auto mb-1 text-blue-100" />
                    <p className="text-xl font-bold">50+ yrs</p>
                    <p className="text-xs text-blue-100/90">Combined Experience</p>
                  </div>
                  <div className="bg-white/10 rounded-2xl px-4 py-3 text-center">
                    <Award className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                    <p className="text-xl font-bold">15,000+</p>
                    <p className="text-xs text-blue-100/90">Successful Procedures</p>
                  </div>
                  <div className="bg-white/10 rounded-2xl px-4 py-3 text-center">
                    <Star className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                    <p className="text-xl font-bold">98%</p>
                    <p className="text-xs text-blue-100/90">Patient Satisfaction</p>
                  </div>
                </div>
                <p className="text-xs text-blue-100/80">
                  Every surgery is supported by a multidisciplinary command center—pre-surgery planning, safe anesthesia, precision operation, and guided rehabilitation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights + Leadership */}
      <section className="py-12 bg-white">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {heroStats.map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-blue-100 via-blue-50 to-white rounded-3xl border border-blue-200 px-5 py-5 text-left shadow-md">
                <item.icon className="w-6 h-6 text-blue-700 mb-3" />
                <p className="text-xs text-blue-700 uppercase tracking-wide font-semibold">{item.label}</p>
                <p className="text-base font-semibold text-gray-900 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {cultureHighlights.map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white to-blue-50 rounded-3xl border border-blue-100 p-6 shadow-md hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-blue-100 via-blue-50 to-white border border-blue-200 rounded-3xl shadow-xl p-6 md:p-8">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-blue-600">Collaborative care units</p>
                <h3 className="text-2xl font-bold text-gray-900">Integrated teams dedicated to each specialty</h3>
              </div>
              <span className="text-sm text-blue-600 font-semibold">Interdisciplinary command centers</span>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {collaborativeUnits.map((unit, idx) => (
                <div key={idx} className="rounded-2xl border border-gray-100 px-4 py-4 hover:border-blue-200 transition-colors bg-blue-50/40">
                  <p className="text-base font-semibold text-gray-900">{unit.title}</p>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{unit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Doctors Heading */}
      <section className="py-4 bg-white">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-600 bg-clip-text text-transparent">
            Our Doctors
          </h2>
          <div className="w-32 h-1 mx-auto rounded-full bg-gradient-to-r from-blue-600 via-sky-500 to-purple-600" />
          <p className="text-gray-700 max-w-3xl mx-auto text-sm md:text-lg">
            The team behind every successful surgery, from first consultation to complete rehabilitation.
          </p>
        </div>
      </section>

      {/* Full Team */}
      <section className="py-4 bg-white">
        <div className="max-w-screen-2xl mx-auto px-2 sm:px-4 lg:px-6">
          <MedicalTeamSection showHeading={false} />
        </div>
      </section>

      {/* Philosophy + Workflow */}
      <section className="relative py-16 bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, rgba(59,130,246,0.15), transparent 45%),
                            radial-gradient(circle at 80% 0%, rgba(99,102,241,0.12), transparent 40%)`
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(rgba(148,163,184,0.15) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(148,163,184,0.1) 1px, transparent 1px)`,
          backgroundSize: '140px 140px'
        }} />
        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100/70 border border-blue-200 text-sm font-semibold text-blue-800">
              Refined Orthopedic Care
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Our Care Philosophy
            </h2>
            <div className="w-16 h-1 mx-auto rounded-full bg-gradient-to-r from-blue-500 to-purple-500"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Collaborative, evidence-based, and deeply human. Every patient journey is led by experts who listen first.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { emoji: '🤝', color: 'from-blue-500 to-indigo-500', title: 'Collaborative Care', text: 'Multidisciplinary team ensures comprehensive treatment.' },
                { emoji: '📚', color: 'from-emerald-500 to-green-500', title: 'Continuous Learning', text: 'Clinicians stay aligned with global medical advances.' },
                { emoji: '❤️', color: 'from-rose-500 to-pink-500', title: 'Patient-Centered', text: 'Every decision is guided by empathy and clear communication.' },
                { emoji: '⚕️', color: 'from-purple-500 to-blue-500', title: 'Evidence Driven', text: 'Protocols validated by national and international bodies.' }
              ].map((card) => (
                <div key={card.title} className="text-center bg-white rounded-3xl shadow-lg border border-gray-100 p-8 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300">
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${card.color} text-white flex items-center justify-center text-3xl`}>
                    {card.emoji}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{card.title}</h3>
                  <p className="text-gray-600">{card.text}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">How We Work With You</h3>
              <ol className="space-y-5">
                {workflow.map((step) => (
                  <li key={step.title} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 font-bold flex items-center justify-center flex-shrink-0">
                      {step.step}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{step.title}</p>
                      <p className="text-gray-600 text-sm">{step.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary-900 via-primary-700 to-blue-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 15% 20%, rgba(255,255,255,0.25), transparent 45%),
                          radial-gradient(circle at 85% 0%, rgba(255,255,255,0.15), transparent 38%)`
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
                          linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)`,
          backgroundSize: '160px 160px'
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-5">
              <p className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 border border-white/30 text-sm font-semibold">
                Schedule a Consultation with Our Experts
              </p>
              <h2 className="text-3xl md:text-4xl font-bold leading-snug">
                Choose the specialist that's right for you and book your appointment today.
              </h2>
              <p className="text-blue-100 text-lg">
                Same-week consults, coordinated anesthesia review, and rehabilitation planning begin from your first visit.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-sm font-semibold">
                <div className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3">
                  <p className="text-blue-100 uppercase tracking-wider text-xs">Clinic Hours</p>
                  <p className="text-white text-lg mt-1">Mon - Sun • 8 AM – 8 PM</p>
                </div>
                <div className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3">
                  <p className="text-blue-100 uppercase tracking-wider text-xs">Emergency Hotline</p>
                  <p className="text-white text-lg mt-1">24/7 Orthopedic Command</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white text-blue-700 font-semibold shadow-lg hover:bg-slate-100 transition-colors"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Book Appointment
                </a>
                <a
                  href="tel:+917727048951"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/60 bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors shadow-md"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call: +91 77270 48951
                </a>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="bg-white/10 border border-white/15 rounded-3xl p-5 shadow-xl">
                <p className="text-sm uppercase tracking-wider text-blue-100">Before your visit</p>
                <ul className="mt-4 space-y-3 text-blue-50 text-sm">
                  {['Rapid imaging & labs coordination', 'Insurance verification support', 'Dedicated patient concierge'].map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-300 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 border border-white/15 rounded-3xl p-5 shadow-xl">
                <p className="text-sm uppercase tracking-wider text-blue-100">After your visit</p>
                <ul className="mt-4 space-y-3 text-blue-50 text-sm">
                  {['Post-surgery follow-up scheduling', 'Rehab kit & tele-physio guidance', '24/7 consultant hotline'].map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-300 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurTeam;
